import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { reportAPI } from '../../api/reports';
import { Download, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

interface DebtRow {
  entityType: 'SUPPLIER' | 'CUSTOMER';
  entityId: number;
  name: string;
  totalDebt: number;
  tripCount: number;
}

interface ReportData {
  supplierDebts: DebtRow[];
  customerDebts: DebtRow[];
  summary: {
    totalSupplierDebt: number;
    totalCustomerDebt: number;
    totalDebt: number;
  };
}

export const DebtStatus: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'supplier' | 'customer'>('supplier');

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const result = await reportAPI.debtStatus();
      setData(result);
    } catch (error) {
      toast.error(t('reports.generateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await reportAPI.exportExcel({
        type: 'debt-status',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-debt-status-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(t('reports.exportSuccess'));
    } catch (error) {
      toast.error(t('reports.exportError'));
    }
  };

  if (loading) return <div className="text-center py-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('reports.debtStatus')}</h1>
        <div className="flex space-x-2">
          <button
            onClick={loadReport}
            className="btn-secondary flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('common.refresh')}
          </button>
          {data && (
            <button
              onClick={handleExport}
              className="btn-primary flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('reports.exportExcel')}
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('reports.supplierDebt')}</p>
            <p className="text-2xl font-bold text-orange-600">
              {formatCurrency(data.summary.totalSupplierDebt)} đ
            </p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('reports.customerDebt')}</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatCurrency(data.summary.totalCustomerDebt)} đ
            </p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('reports.totalDebt')}</p>
            <p className="text-2xl font-bold text-red-600">
              {formatCurrency(data.summary.totalDebt)} đ
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="card">
        <div className="flex border-b mb-4">
          <button
            onClick={() => setActiveTab('supplier')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'supplier'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500'
            }`}
          >
            {t('nav.suppliers')} ({data?.supplierDebts.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('customer')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'customer'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500'
            }`}
          >
            {t('nav.customers')} ({data?.customerDebts.length || 0})
          </button>
        </div>

        {/* Supplier Debts */}
        {activeTab === 'supplier' && (
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr className="text-left">
                <th className="pb-3 table-header">{t('nav.suppliers')}</th>
                <th className="pb-3 table-header text-right">{t('reports.tripCount')}</th>
                <th className="pb-3 table-header text-right">{t('reports.debt')}</th>
              </tr>
            </thead>
            <tbody>
              {data?.supplierDebts.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-8 text-center text-gray-500">
                    {t('common.noData')}
                  </td>
                </tr>
              ) : (
                data?.supplierDebts.map((row) => (
                  <tr key={row.entityId} className="border-b last:border-0">
                    <td className="py-3 font-medium">{row.name}</td>
                    <td className="py-3 text-right">{row.tripCount}</td>
                    <td className="py-3 text-right font-semibold text-orange-600">
                      {formatCurrency(row.totalDebt)} đ
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* Customer Debts */}
        {activeTab === 'customer' && (
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr className="text-left">
                <th className="pb-3 table-header">{t('nav.customers')}</th>
                <th className="pb-3 table-header text-right">{t('reports.tripCount')}</th>
                <th className="pb-3 table-header text-right">{t('reports.debt')}</th>
              </tr>
            </thead>
            <tbody>
              {data?.customerDebts.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-8 text-center text-gray-500">
                    {t('common.noData')}
                  </td>
                </tr>
              ) : (
                data?.customerDebts.map((row) => (
                  <tr key={row.entityId} className="border-b last:border-0">
                    <td className="py-3 font-medium">{row.name}</td>
                    <td className="py-3 text-right">{row.tripCount}</td>
                    <td className="py-3 text-right font-semibold text-blue-600">
                      {formatCurrency(row.totalDebt)} đ
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
